<template>
  <v-dialog
    :value="showImportModal"
    @input="$emit('update:showImportModal', $event)"
    max-width="700px"
    persistent
    @click:outside="$emit('close-import-modal')"
  >
    <v-card class="pa-0">
      <div class="pa-4 d-flex justify-space-between align-center header-modal">
        <h3 class="mb-0">Add reviews</h3>
        <v-btn icon @click="$emit('close-import-modal')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="content-modal">
        <v-card>
          <v-tabs
            v-model="importTabProxy"
            background-color="primary"
            dark
            style="border-radius: 0 !important; position: sticky; top: 0; z-index: 10"
          >
            <v-tab>Manual</v-tab>
            <v-tab>Random</v-tab>
            <v-tab>Import from CSV</v-tab>
          </v-tabs>
          <v-tabs-items v-model="importTabProxy">
            <!-- Manual tab -->
            <v-tab-item>
              <div class="pa-4">
                <ReviewAddForm
                  v-model="manualReviewProxy"
                  :typeReview="typeReview"
                  :editor="editor"
                  :editorConfig="editorConfig"
                  @add-image="$emit('manual-add-image')"
                  @preview-image="$emit('manual-preview-image', $event)"
                  @edit-image="$emit('manual-edit-image', $event)"
                />
              </div>
            </v-tab-item>
            <!-- Random tab -->
            <v-tab-item>
              <div class="pa-4">
                <div>
                  <p class="mb-2">Enter the number of reviews you want to generate</p>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="randomReviewsCountProxy"
                        type="number"
                        min="1"
                        :max="maxRandomReviewsCount"
                        label="Random Reviews Count"
                        :rules="[
                          v =>
                            (v >= 1 && v <= maxRandomReviewsCount) ||
                            `Random reviews count must be between 1 and ${maxRandomReviewsCount}`,
                        ]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select v-model="selectedGenderProxy" :items="genderOptions" label="Gender" />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-tab-item>
            <!-- CSV tab -->
            <v-tab-item>
              <div class="pa-4">
                <h4 class="mb-4">Upload CSV File</h4>
                <v-form :value="formValid" lazy-validation>
                  <div
                    class="upload-zone"
                    @dragover.prevent
                    @dragenter.prevent="$emit('set-dragging', true)"
                    @dragleave.prevent="$emit('set-dragging', false)"
                    @drop.prevent="$emit('drop-file', $event)"
                    :class="{ 'upload-zone--active': isDragging }"
                  >
                    <v-icon large color="primary">mdi-file-upload</v-icon>
                    <p class="mb-2">Drag & drop your CSV file here</p>
                    <v-btn small outlined color="primary" @click="$refs.csvInput.click()"> Browse Files </v-btn>
                    <input
                      type="file"
                      ref="csvInput"
                      accept=".csv"
                      style="display: none"
                      @change="$emit('file-select', $event)"
                    />
                  </div>
                  <div v-if="csvFile && !csvLoading" class="mt-4 d-flex align-center">
                    <v-chip color="primary" text-color="white">
                      {{ csvFile.name }}
                      <v-icon right small @click="$emit('remove-csv-file')">mdi-close</v-icon>
                    </v-chip>
                    <div class="ml-4 primary--text">
                      <span>Finished importing:</span> {{ importedReviews.length }} reviews
                      <span v-if="duplicateCount > 0">
                        ({{ importedReviews.length - duplicateCount }} valid, {{ duplicateCount }}
                        {{ duplicateCount > 1 ? 'duplicates' : 'duplicate' }})
                      </span>
                    </div>
                  </div>
                  <v-alert v-if="!csvFile && !csvLoading" type="info" class="mt-4" dense border="left" colored-border>
                    Please upload a CSV file containing your product reviews data.
                  </v-alert>
                  <div class="mt-4">
                    Download a
                    <a
                      target="_blank"
                      class="primary--text"
                      :href="csvTemplateUrl + '?v=' + new Date().getTime()"
                      download
                      >sample CSV template</a
                    >
                    to see an example of the format required.
                  </div>
                  <v-alert v-if="csvError" type="error" class="mt-2" dense border="left" colored-border>
                    <div v-html="csvError" style="color: red"></div>
                  </v-alert>
                </v-form>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </div>
      <div class="footer-modal">
        <v-card-actions>
          <v-spacer></v-spacer>
          <div class="d-flex justify-end py-2">
            <v-btn @click="$emit('close-import-modal')">Close</v-btn>
            <v-btn
              @click="$emit('save-add-reviews')"
              :disabled="
                (importTabProxy === 0 && (!manualReviewProxy || !manualReviewProxy.fullName)) ||
                  (importTabProxy === 1 &&
                    (!randomReviewsCountProxy ||
                      randomReviewsCountProxy < 1 ||
                      randomReviewsCountProxy > maxRandomReviewsCount)) ||
                  (importTabProxy === 2 && (!csvFile || !importedReviews.length))
              "
              color="primary"
              class="ml-4"
            >
              Save
            </v-btn>
          </div>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import ReviewAddForm from './review-add-form.vue';

export default {
  name: 'ImportReviewsDialog',
  components: { ReviewAddForm },
  props: {
    typeReview: { type: String, default: 'default' },
    showImportModal: { type: Boolean, required: true },
    importTab: { type: Number, required: true },
    manualReview: { type: Object, required: true },
    editor: { type: Function, required: true },
    editorConfig: { type: Object, required: true },
    randomReviewsCount: { type: Number, required: true },
    maxRandomReviewsCount: { type: Number, required: true },
    selectedGender: { type: String, required: true },
    genderOptions: { type: Array, required: true },
    formValid: { type: Boolean, required: true },
    isDragging: { type: Boolean, required: true },
    csvFile: { type: [File, Object, null], default: null },
    csvLoading: { type: Boolean, required: true },
    csvError: { type: [String, null], default: null },
    importedReviews: { type: Array, required: true },
    duplicateCount: { type: Number, required: true },
    csvTemplateUrl: { type: String, default: 'https://static.cdnstash.com/assets/csv/product-reviews-template.csv' },
  },
  computed: {
    manualReviewProxy: {
      get() {
        return this.manualReview;
      },
      set(v) {
        this.$emit('update:manualReview', v);
      },
    },
    importTabProxy: {
      get() {
        return this.importTab;
      },
      set(v) {
        this.$emit('update:importTab', v);
      },
    },
    randomReviewsCountProxy: {
      get() {
        return this.randomReviewsCount;
      },
      set(v) {
        const parsed = typeof v === 'string' ? parseInt(v, 10) : v;
        this.$emit('update:randomReviewsCount', isNaN(parsed) ? 0 : parsed);
      },
    },
    selectedGenderProxy: {
      get() {
        return this.selectedGender;
      },
      set(v) {
        this.$emit('update:selectedGender', v);
      },
    },
  },
};
</script>

<style scoped lang="scss">
.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.content-modal {
  max-height: 70vh;
  overflow-y: auto;
}

.upload-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: 0.2s;
}

.upload-zone--active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}
</style>
